export function buildEntryGuideReferenceSnapshot(entryGuideData) {
  if (!entryGuideData) return null;

  const linkedInvoiceDocuments = Array.isArray(entryGuideData.linked_invoice_documents)
    ? entryGuideData.linked_invoice_documents
    : [];

  return {
    purchase_order_id: entryGuideData.purchase_order_id || null,
    requirement_id: entryGuideData.requirement_id || null,
    document_id: entryGuideData.document_id || null,
    linked_invoice_documents: linkedInvoiceDocuments,
    items: (entryGuideData.items || []).map((item) => ({
      id: item.id,
      product_id: item.product_id,
      purchase_order_item_id: item.purchase_order_item_id || null,
      document_item_id: item.document_item_id || null,
      requirement_item_id: item.requirement_item_id || null,
    })),
    labels: {
      purchase_order: entryGuideData.purchase_order?.code || null,
      document: entryGuideData.document?.code || null,
      requirement: entryGuideData.requirement?.code || null,
      linked_invoices: linkedInvoiceDocuments
        .map((doc) => doc.reference_label || doc.code)
        .filter(Boolean),
    },
  };
}

export function hasEntryGuideReference(snapshot) {
  if (!snapshot) return false;

  return Boolean(
    snapshot.purchase_order_id
    || snapshot.requirement_id
    || snapshot.document_id
    || (snapshot.linked_invoice_documents && snapshot.linked_invoice_documents.length > 0)
    || snapshot.items.some(
      (item) => item.purchase_order_item_id || item.document_item_id || item.requirement_item_id,
    ),
  );
}

export function getEntryGuideReferenceLabel(snapshot) {
  if (!snapshot) return null;
  const linked = snapshot.labels?.linked_invoices?.[0];
  return linked
    || snapshot.labels.document
    || snapshot.labels.purchase_order
    || snapshot.labels.requirement
    || null;
}

export function resolveSubmitReferenceIds(formData, snapshot) {
  const resolve = (formKey) => {
    const formValue = formData[formKey];
    if (formValue && formValue !== '') return parseInt(formValue, 10);
    const original = snapshot?.[formKey];
    if (original) return parseInt(original, 10);
    return null;
  };

  return {
    purchase_order_id: resolve('purchase_order_id'),
    requirement_id: resolve('requirement_id'),
    document_id: resolve('document_id'),
  };
}

export function analyzeEntryGuideReferenceChanges(formData, snapshot) {
  if (!snapshot || !hasEntryGuideReference(snapshot)) {
    return {
      willLoseHeaderReference: false,
      willLoseItemReferences: [],
      refs: resolveSubmitReferenceIds(formData, snapshot),
      safeEdit: true,
    };
  }

  const refs = resolveSubmitReferenceIds(formData, snapshot);
  const originalHad = {
    purchase_order: Boolean(snapshot.purchase_order_id),
    document: Boolean(snapshot.document_id),
    requirement: Boolean(snapshot.requirement_id),
  };
  const willHave = {
    purchase_order: Boolean(refs.purchase_order_id),
    document: Boolean(refs.document_id),
    requirement: Boolean(refs.requirement_id),
  };

  const willLoseHeaderReference = (
    (originalHad.purchase_order && !willHave.purchase_order)
    || (originalHad.document && !willHave.document)
    || (originalHad.requirement && !willHave.requirement)
  );

  const willLoseItemReferences = [];
  const snapshotItemsById = new Map(
    snapshot.items.filter((item) => item.id).map((item) => [item.id, item]),
  );
  const currentIds = new Set(formData.items.map((item) => item.id).filter(Boolean));

  formData.items.forEach((item, index) => {
    if (!item.id) return;
    const original = snapshotItemsById.get(item.id);
    if (!original) return;

    const hadItemRef = Boolean(
      original.purchase_order_item_id
      || original.document_item_id
      || original.requirement_item_id,
    );
    const hasItemRef = Boolean(
      item.purchase_order_item_id
      || item.document_item_id
      || item.requirement_item_id,
    );
    const productChanged = String(original.product_id) !== String(item.product_id);

    if (hadItemRef && (productChanged || !hasItemRef)) {
      willLoseItemReferences.push({ index, productChanged, removed: false });
    }
  });

  snapshot.items.forEach((original) => {
    if (!original.id || currentIds.has(original.id)) return;
    if (original.purchase_order_item_id || original.document_item_id || original.requirement_item_id) {
      willLoseItemReferences.push({ index: -1, removed: true });
    }
  });

  return {
    willLoseHeaderReference,
    willLoseItemReferences,
    refs,
    safeEdit: !willLoseHeaderReference && willLoseItemReferences.length === 0,
  };
}

export function buildReferenceEditInfoMessage() {
  return 'La referencia se conserva al editar fecha, hora, observaciones, proveedor o cantidades permitidas. Se perderá si cambia el producto de una línea vinculada, elimina líneas referenciadas o quita la referencia. Esto incluye guías facturadas desde documentos de compra.';
}

export function buildReferenceLossConfirmMessage(analysis, snapshot) {
  const parts = [];
  const label = getEntryGuideReferenceLabel(snapshot);

  if (analysis.willLoseHeaderReference) {
    parts.push(`Se perderá la referencia de la guía${label ? ` (${label})` : ''}.`);
  }
  if (analysis.willLoseItemReferences.length > 0) {
    parts.push(`${analysis.willLoseItemReferences.length} línea(s) dejarán de estar vinculadas al documento de origen.`);
  }
  parts.push('¿Desea guardar de todos modos?');

  return parts.join('\n\n');
}

export function itemHasReferenceLink(item) {
  return Boolean(
    item?.purchase_order_item_id
    || item?.document_item_id
    || item?.requirement_item_id,
  );
}

export function clearItemReferenceLinks(item) {
  return {
    ...item,
    purchase_order_item_id: null,
    document_item_id: null,
    requirement_item_id: null,
    purchase_order_id: null,
    requirement_id: null,
  };
}
